import React from 'react';

import { normalize, clamp, roundTo } from '@/utils';
import useMousePositionWithinElement from '@/hooks/use-mouse-position-within-element';

export function usePointerInputMechanism({
  wrapperRef,
  onChange,
}: {
  wrapperRef: React.RefObject<HTMLDivElement>;
  onChange: (params: {
    saturation: number;
    lightness: number;
  }) => void;
}) {
  const [isMouseDown, setIsMouseDown] = React.useState(false);

  const [mousePosition, boundingBox] = useMousePositionWithinElement(
    wrapperRef,
    {
      buffer: 250,
      throttleBy: 20,
    }
  );

  // Primary input mechanism: using a mouse or pointer device
  React.useEffect(() => {
    if (
      !isMouseDown ||
      typeof mousePosition.x !== 'number' ||
      typeof mousePosition.y !== 'number' ||
      !boundingBox
    ) {
      return;
    }

    const saturation = roundTo(
      clamp(
        normalize(mousePosition.x, 0, boundingBox.width, 0, 1),
        0,
        1
      ),
      3
    );

    const lightness = roundTo(
      clamp(
        normalize(mousePosition.y, 0, boundingBox.height, 1, 0),
        0,
        1
      ),
      3
    );

    onChange({
      saturation,
      lightness,
    });
  }, [isMouseDown, mousePosition, boundingBox, onChange]);

  React.useEffect(() => {
    function handleMouseUp() {
      setIsMouseDown(false);
    }

    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('touchend', handleMouseUp);
    document.addEventListener('mouseleave', handleMouseUp);

    return () => {
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchend', handleMouseUp);
      document.removeEventListener('mouseleave', handleMouseUp);
    };
  }, [isMouseDown]);

  const startTracking = (ev: React.MouseEvent | React.TouchEvent) => {
    // Disable text selection
    ev.preventDefault();
    ev.stopPropagation();

    setIsMouseDown(true);
  };

  return { isTracking: isMouseDown, startTracking };
}
